import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  TextField,
  Typography,
  InputAdornment,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Api from '../../../../Services/Api';

const PageSelector = ({ onPageSelect, selectedPage, selectedPages = [], pages: externalPages, quickLaunchMode = false, enableSearch = false, multiSelect = false, useRsocSpy = false }) => {
  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pages, setPages] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Load pages on mount and when search changes
  useEffect(() => {
    // If external pages are provided AND search is disabled, use those instead
    if (externalPages && !enableSearch) {
      setPages(externalPages);
      setLoading(false);
      return;
    }

    const fetchPages = async () => {
      setLoading(true);
      try {
        const params = {
          search: searchQuery,
        };

        // Choose API method based on useRsocSpy prop
        const response = useRsocSpy
          ? await Api.getRsocSpyPages(params)
          : await Api.getRsocPages(params);

        if (response && response.rsoc_pages) {
          const fetchedPages = response.rsoc_pages.data || [];
          setPages(fetchedPages);
        } else {
          setPages([]);
          setError('No RSOC pages found');
        }
      } catch (err) {
        console.error('Error fetching RSOC pages:', err);
        setError('Error loading RSOC pages: ' + (err.message || 'Unknown error'));
        setPages([]);
      } finally {
        setLoading(false);
      }
    };

    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Set new timeout for debouncing
    const timeoutId = setTimeout(fetchPages, 500);
    setSearchTimeout(timeoutId);

    // Cleanup on unmount
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchQuery, externalPages, enableSearch, useRsocSpy]);

  // Handle search input change
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  // Handle page selection
  const handlePageSelect = (page) => {
    console.log('handlePageSelect called:', { page: page.title, multiSelect, selectedPages: selectedPages?.map(p => p.title) });
    
    if (multiSelect) {
      // For multi-select, toggle the page
      const isSelected = selectedPages?.some(p => p.id === page.id);
      console.log(`Page ${page.title} is currently selected:`, isSelected);
      
      if (isSelected) {
        // Remove page
        const newSelection = selectedPages.filter(p => p.id !== page.id);
        console.log('Removing page, new selection:', newSelection.map(p => p.title));
        onPageSelect(newSelection);
      } else {
        // Add page
        const newSelection = [...(selectedPages || []), page];
        console.log('Adding page, new selection:', newSelection.map(p => p.title));
        onPageSelect(newSelection);
      }
    } else {
      // Single select
      onPageSelect(page);
    }
  };

  // Check if a page is selected
  const isPageSelected = (page) => {
    if (multiSelect) {
      const isSelected = selectedPages?.some(p => p.id === page.id) || false;
      console.log(`Checking if page ${page.id} (${page.title}) is selected:`, isSelected, 'selectedPages:', selectedPages?.map(p => p.id));
      return isSelected;
    }
    return selectedPage?.id === page.id;
  };

  return (
    <Box>
      <Grid container spacing={2}>
        {/* Search Bar - Show when not quick launch mode OR when quick launch has search enabled */}
        {(!quickLaunchMode || (quickLaunchMode && enableSearch)) && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Search Pages"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Search by title or keywords..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        )}

        {/* Error Alert */}
        {error && (
          <Grid item xs={12}>
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          </Grid>
        )}

        {/* Pages Grid */}
        <Grid item xs={12}>
          <Box sx={{ mt: 2 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : pages.length === 0 ? (
              <Box sx={{ textAlign: 'center', p: 4 }}>
                <Typography color="text.secondary">
                  {searchQuery
                    ? 'No pages found matching your search'
                    : 'No pages available. Create some pages in the Generator first.'}
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {pages.map((page) => (
                  <Grid item xs={12} sm={6} md={4} key={page.id}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        border: isPageSelected(page) ? 2 : 1,
                        borderColor: isPageSelected(page) ? 'primary.main' : 'divider',
                        backgroundColor: isPageSelected(page) ? 'action.selected' : 'background.paper',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: 2,
                        },
                      }}
                      onClick={() => handlePageSelect(page)}
                    >
                      <CardContent>
                        <Typography variant="h6" gutterBottom noWrap>
                          {page.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Created: {new Date(page.created_at).toLocaleDateString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          Keywords: {Array.isArray(page.keywords) ? page.keywords.join(', ') : (page.keywords || 'None')}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PageSelector; 